<template>
  <div class="pfa-page">
    <!-- 顶部搜索栏 -->
    <div class="pfa-header">
      <img src="@/assets/images/home/logo@2x.png" class="logo" alt="iEP" />
      <form action="/" class="search-form">
        <van-search
          v-model.trim="searchValue"
          placeholder="请输入搜索内容"
          @search="onSearch"
          :clearable="true"
          clear-trigger="always"
          @clear="onClearSearch"
          @click.native="handleSearchClick"
        />
      </form>
    </div>

    <!-- 标签页 -->
    <div class="tab-scroll-wrapper" ref="tabScrollWrapper">
      <div
        v-for="item in categoryList"
        :key="item.id"
        class="tab-scroll-item"
        :class="{ cur: courseCategoryIdFirst === item.id }"
        @click="switchTab(item)"
      >
        {{ item.name }}
        <img v-if="courseCategoryIdFirst === item.id" src="@/assets/images/training/img(6).png" class="img-btm" alt="">
      </div>
    </div>

    <!-- 内容列表 -->
    <div class="content-wrapper">
        <van-list
          :loading="state.loading"
          :finished="state.finished"
          :finished-text="state.finishText"
          @load="getContentList"
          v-if="contentList && contentList.length > 0"
        >
          <div
            v-for="(item, index) in contentList"
            :key="index"
            class="content-item"
            @click="goDetail(item)"
          >
            <div class="content-info">
              <div class="content-title">{{ item.name }}</div>
              <div class="content-meta-row">
                <span>作者：{{ item.lecturers }}</span>
                <span class="content-views">
                  <img src="@/assets/images/home/search_view@2x.png" class="view-icon" alt="" />
                  {{ item.playNum }}
                </span>
              </div>
              <div class="content-meta">医院：{{ item.lecturerHospitals }}</div>
            </div>
            <div class="content-image">
              <img :src="item.coverMaterialUrl" alt="" />
              <div class="content-type-tag">
                <img :src="getCategoryTypeIcon(item.type)" class="tag-icon" alt="" />
                {{ getCategoryTypeName(item.type) }}
              </div>
            </div>
          </div>
        </van-list>
        <div v-if="state.showEmptyView && contentList.length === 0" class="empty-wrapper">
          <EmptyView
            class="empty-view"
            title="暂无数据"
            :description="''"
          />
        </div>
    </div>

  </div>
</template>

<script>
import { getPfaCategoryTree, getPfaPageList } from '@/api/pfa.js'
import withRouteTracking from '@/utils/withRouteTracking'
import { setSha } from '@/utils/util'
import { Toast } from 'vant'
import {isScorm} from '../../api/scorm'

const PFA_PAGE_STATE_KEY = 'pfaPageState'

const UserProfile = {
  name: 'PFA',
  data() {
    return {
      searchValue: '',
      categoryList: [],
      courseCategoryIdFirst: '',
      contentList: [],
      state: {
        pageSize: 10,
        pageNum: 1,
        loading: false,
        finished: false,
        finishText: '已经到底了',
        showEmptyView: false
      },
      savedScrollTop: 0
    }
  },
  computed: {
    userInfo() {
      return this.$store.getters.userInfo
    }
  },
  async mounted() {
    const restored = await this.restorePageState()
    if (restored) {
      return
    }

    window.sessionStorage.removeItem('pfaFilter')

    // 从 URL query 参数读取
    const { categoryFirst } = this.$route.query
    if (categoryFirst) {
      this.courseCategoryIdFirst = categoryFirst
    }

    // 加载分类树
    await this.getCategoryTree()

    const hasCurrentCategory = this.categoryList.some(item => item.id === this.courseCategoryIdFirst)

    // 如果当前标签不存在，则默认使用第一个
    if ((!this.courseCategoryIdFirst || !hasCurrentCategory) && this.categoryList.length > 0) {
      this.courseCategoryIdFirst = this.categoryList[0].id
    }

    // 重置并加载数据
    this.state.pageNum = 1
    this.contentList = []
    await this.getContentList()
  },
  activated() {
    // keep-alive 激活时恢复滚动位置
    this.$nextTick(() => {
      const wrapper = document.querySelector('.content-wrapper')
      if (wrapper && this.savedScrollTop) {
        wrapper.scrollTop = this.savedScrollTop
      }
    })
  },
  deactivated() {
    // keep-alive 停用时保存滚动位置
    const wrapper = document.querySelector('.content-wrapper')
    if (wrapper) {
      this.savedScrollTop = wrapper.scrollTop
    }
  },
  methods: {
    async restorePageState() {
      const savedState = window.sessionStorage.getItem(PFA_PAGE_STATE_KEY)
      if (!savedState) return false

      try {
        const {
          categoryId,
          searchValue = '',
          scrollTop = 0,
          contentList = [],
          pageNum = 1,
          finished = false
        } = JSON.parse(savedState)

        this.courseCategoryIdFirst = categoryId
        this.searchValue = searchValue
        this.contentList = contentList
        this.state.pageNum = pageNum
        this.state.finished = finished
        this.state.showEmptyView = contentList.length === 0

        await this.getCategoryTree()

        this.$nextTick(() => {
          const wrapper = document.querySelector('.content-wrapper')
          if (wrapper) {
            wrapper.scrollTop = scrollTop
          }
        })

        return true
      } catch (error) {
        console.error('恢复状态失败:', error)
        return false
      } finally {
        // 恢复完成后立即清除缓存，避免下次进入误用旧数据
        window.sessionStorage.removeItem(PFA_PAGE_STATE_KEY)
      }
    },

    savePageState() {
      const wrapper = document.querySelector('.content-wrapper')
      const scrollTop = wrapper ? wrapper.scrollTop : 0

      window.sessionStorage.setItem(PFA_PAGE_STATE_KEY, JSON.stringify({
        categoryId: this.courseCategoryIdFirst,
        searchValue: this.searchValue,
        scrollTop,
        contentList: this.contentList,
        pageNum: this.state.pageNum,
        finished: this.state.finished
      }))
    },

    // type 映射：1-文献 2-课程 3-病例
    getCategoryTypeName(type) {
      const map = { 1: '文献', 2: '课程', 3: '病例' }
      return map[type] || ''
    },

    // 获取类型图标
    getCategoryTypeIcon(type) {
      const iconMap = {
        1: require('@/assets/images/home/search_view@2x.png'),
        2: require('@/assets/images/home/search_course@2x.png'),
        3: require('@/assets/images/home/search_case@2x.png')
      }
      return iconMap[type] || iconMap[1]
    },

    // 获取分类树
    async getCategoryTree() {
      try {
        const res = await getPfaCategoryTree()
        if (res.code === 0 && res.data) {
          this.categoryList = res.data
        }
      } catch (error) {
        console.error('获取PFA分类失败:', error)
      }
    },

    // 搜索
    onSearch() {
      this.resetAndFetchList()
    },

    // 清除搜索
    onClearSearch() {
      this.searchValue = ''
      this.resetAndFetchList()
    },

    // Vant2 clear 图标仅绑定 touchstart，PC 端点击时做兜底处理
    handleSearchClick(event) {
      const target = event && event.target
      if (!target || !target.classList) return
      if (!target.classList.contains('van-field__clear')) return
      if (!this.searchValue) return
      this.onClearSearch()
    },

    // 标签页切换
    switchTab(item) {
      if (this.courseCategoryIdFirst === item.id) return
      this.courseCategoryIdFirst = item.id
      this.resetAndFetchList()
    },

    // 重置并加载列表
    resetAndFetchList() {
      this.state.loading = false
      this.state.finished = false
      this.state.showEmptyView = false
      this.state.pageNum = 1
      this.contentList = []
      this.getContentList()
    },

    // 加载内容列表（van-list @load 触发）
    async getContentList() {
      if (this.state.loading) return
      this.state.showEmptyView = false
      this.state.loading = true
      Toast.loading({ message: '加载中...' })

      const categoryId = this.courseCategoryIdFirst

      if (!categoryId) {
        this.contentList = []
        this.state.showEmptyView = true
        this.state.finished = true
        this.state.loading = false
        Toast.clear()
        return
      }

      const params = {
        pageNum: this.state.pageNum,
        pageSize: this.state.pageSize,
        dataValue: this.searchValue,
        categoryIds: [categoryId]
      }

      try {
        const res = await getPfaPageList(params)

        if (res.code !== 0 || !res.data || !res.data.list) {
          this.contentList = []
          this.state.finished = true
          this.state.showEmptyView = true
        } else {
          this.contentList = [...this.contentList, ...res.data.list]
          this.state.finished = res.data.total === 0 ? true : this.contentList.length >= res.data.total
          this.state.showEmptyView = this.contentList.length === 0
        }
        this.state.pageNum++
      } catch (error) {
        console.error('获取PFA内容列表失败:', error)
        Toast.fail({ message: '加载失败，请稍后再试' })
      } finally {
        this.state.loading = false
        Toast.clear()
      }
    },

    // 跳转详情
    async goDetail(item) {
      if (item.type === 2) {
        const query = {
          courseId: item.id,
          businessId: item.courseCategoryId,
          entrance: '163',
          channel: '163',
          fromPage: 'pfa'
        }
        const isscorm = await isScorm(item.id)
        let path = '/courserDetails'
        if (isscorm.data) {
          path = '/scormCourseDetail'
        }
        this.$router.push({path, query})
      } else if (item.type === 1) {
        // 文献
        this.$router.push({
          path: '/articleDetails',
          query: { id: item.id, entrance: '163', channel: '163', businessId: item.courseCategoryId }
        })
      } else if (item.type === 3) {
        // 病例 - 保存当前状态后跳转
        this.savePageState()

        const openidBindli = `&unionid=${this.userInfo.unionId}&openid=${this.userInfo.iepOpenId}${setSha(`xxbiep${this.userInfo.iepOpenId}`)}`
        window.location.href = `${process.env.bingliUrl}/wap/index.html#/casedetails?id=${item.id}${openidBindli}&entrance=163&businessChannel=163`
      }
    }
  }
}

export default withRouteTracking(UserProfile)
</script>

<style lang="less" scoped>
.pfa-page {
  min-height: 100vh;
  max-height: 100vh;
  background-color: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  // 顶部搜索栏
  .pfa-header {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 10px 16px;
    background-color: #fff;

    .logo {
      width: 24px;
      display: block;
      height: auto;
      margin-right: 10px;
      flex-shrink: 0;
    }

    .search-form {
      flex: 1;
    }
  }
}

/deep/ .pfa-header .van-search {
  padding: 0;
  background: transparent;

  .van-search__content {
    background-color: #f5f5f5;
    border-radius: 16px;
  }

  .van-field__control::placeholder {
    color: #bbb;
    font-size: 13px;
  }
}

// 可滚动标签栏
.tab-scroll-wrapper {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  overflow-x: auto;
  white-space: nowrap;
  padding: 5px 6px 5px;
  background-color: #fff;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }

  .tab-scroll-item {
    position: relative;
    flex-shrink: 0;
    height: 42px;
    padding: 0 10px;
    font-family: PingFang SC, PingFang SC;
    font-weight: 500;
    font-size: 16px;
    color: #343434;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex: 1;
    .img-btm {
      width: 24px;
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
      bottom: 0px;
    }

    &.cur {
      color: #EB1700;
      font-weight: 500;
    }
  }
}

// 内容区域
.content-wrapper {
  flex: 1;
  height: 0;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 12px;

  &::-webkit-scrollbar {
    display: none;
  }

    .empty-wrapper {
      flex: 1;
      display: flex;
      justify-content: center;
      box-sizing: border-box;
    }

    .content-item {
      display: flex;
      align-items: center;
      background-color: #fff;
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 10px;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

      .content-info {
        flex: 1;
        min-width: 0;
        margin-right: 10px;

        .content-title {
          font-size: 14px;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 8px;
          line-height: 20px;
          overflow: hidden;
          text-overflow: ellipsis;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
        }

        .content-meta {
          font-size: 11px;
          color: #999;
          margin-bottom: 4px;
          line-height: 16px;
        }

        .content-meta-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
          font-size: 11px;
          color: #999;
          margin-bottom: 4px;
          line-height: 16px;
        }

        .content-views {
          display: flex;
          align-items: flex-start;

          .view-icon {
            width: 18px;
            display: block;
            margin-right: 3px;
          }
        }
      }

      .content-image {
        position: relative;
        flex-shrink: 0;
        width: 110px;
        height: 60px;
        border-radius: 5px;
        overflow: hidden;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .content-type-tag {
          position: absolute;
          top: 0;
          left: 0;
          background: rgba(0, 0, 0, 0.6);
          color: #fff;
          font-size: 10px;
          padding: 2px 6px;
          border-radius: 6px 0 6px 0;
          display: flex;
          align-items: center;
          gap: 2px;

          .tag-icon {
            width: 10px;
            height: 10px;
            object-fit: contain;
          }
        }
      }
    }
}

/deep/ .content-wrapper .empty-view {
  width: 100%;
  height: auto;
  min-height: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/pfa/pfa.vue